import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { trackMetaEvent } from "../utils/metaTracking";

const SubscriptionSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed
  const pollCount = useRef(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    try {
      const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
      const data = res.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        // Fire Meta CAPI + Pixel Purchase + Subscribe events
        trackMetaEvent(
          "Purchase",
          { value: data.amount, currency: data.currency || "USD", content_ids: [data.plan_id] },
          { order_id: sessionId },
        );
        trackMetaEvent(
          "Subscribe",
          { value: data.amount, currency: data.currency || "USD", content_ids: [data.plan_id] },
          { order_id: sessionId },
        );
        return;
      }
      if (data.status === "expired") {
        setStatus("failed");
        return;
      }

      // Keep polling (max 5 attempts)
      pollCount.current += 1;
      if (pollCount.current < 5) {
        setTimeout(pollStatus, 2000);
      } else {
        setStatus("failed");
      }
    } catch {
      pollCount.current += 1;
      if (pollCount.current < 5) {
        setTimeout(pollStatus, 2000);
      } else {
        setStatus("failed");
      }
    }
  }, [sessionId]);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  return (
    <div className="min-h-screen bg-sky-50 flex items-center justify-center px-4" data-testid="subscription-success-page">
      <div className="fixed top-0 left-1/4 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />

      <div className="glass-card p-10 sm:p-14 max-w-md w-full text-center">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-16 h-16 text-sky-400 mx-auto mb-6 animate-spin" strokeWidth={1.5} />
            <h2 className="text-2xl font-light text-sky-900 mb-2" style={{ fontFamily: "Outfit" }}>
              {t("subscription.verifying")}
            </h2>
            <p className="text-sm text-slate-400">{t("subscription.verifyingDesc")}</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-light text-sky-900 mb-2" style={{ fontFamily: "Outfit" }}>
              {t("subscription.successTitle")}
            </h2>
            <p className="text-sm text-slate-500 mb-8">{t("subscription.successDesc")}</p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="glow-button w-full"
              data-testid="go-to-dashboard-btn"
            >
              {t("subscription.goToDashboard")}
              <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        )}

        {status === "failed" && (
          <div data-testid="payment-failed">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-500" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-light text-sky-900 mb-2" style={{ fontFamily: "Outfit" }}>
              {t("subscription.failedTitle")}
            </h2>
            <p className="text-sm text-slate-500 mb-8">{t("subscription.failedDesc")}</p>
            <Button
              onClick={() => navigate("/subscription")}
              className="glow-button w-full"
              data-testid="try-again-btn"
            >
              {t("subscription.tryAgain")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
